import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { AxiosError } from "axios";
import moment from 'moment';

import {
  DogClubRegistrationDto,
  DogCoownerInfoDto,
  DogDto,
  DogProposedChangesDto,
  IBreed,
  IDog,
  IDogAttachment,
  IDogClubRegistration,
  IDogCoownerInfo,
  IDogProposedChanges,
  IUser,
  DOG_VACCINE_TYPES,
  DogVaccineType,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants"

import { apiService } from "../../../services";
import { helperService } from "../../../services/helper.service";

import StepForm, { Step, useStepForm } from "../StepForm";
import Button from "../../Button/Button";
import Input from "../../Input/Input";
import Checkbox from "../../Checkbox/Checkbox";
import DatePick from "../../DatePick/DatePick";
import Dropdown, { IOption, Provinces, States } from "../../Dropdown/Dropdown";
import OwnerList from "../../OwnerList/OwnerList";
import ClubRegistration from "../../ClubRegistration/ClubRegistration";
import Phone from "../../Phone/Phone";

import scale from "../../../assets/icons/scale.svg";
import num from "../../../assets/icons/num.svg";

import { toast } from "../../Toast/ToastManager";
import { persistDogUpdate } from "../../../registration-requirements";

import './DogForm.scss';
import 'react-image-crop/dist/ReactCrop.css';

const DOG_FORM_STEPS = [
  "Basic Information",
  "Breeder",
  "Owner",
  "Co-Owner Information",
  "Club Specific Information",
  "Image",
  "Health Records",
];

export const DOG_FORM_IMAGE_TAB = DOG_FORM_STEPS.indexOf("Image");

interface IProps {
  onFinish(dog?: IDog): void,
  /** Called whenever the dog is updated in-modal (e.g. attachment deleted) without closing. */
  onDogUpdated?: (dog: IDog) => void,
  dog?: IDog,
  /** Step index to open on. Defaults to Basic Information. */
  initialTab?: number,
  onBehalf?: string,
  onBehalfEmail?: string,
  clubId?: string
  proposedChanges?: IDogProposedChanges | undefined
  eventId?: string
  isAdmin?: boolean  // Whether user is admin/manager for the event's club
}

interface IPendingHealthRecord {
  type: DogVaccineType | "";
  expiry_date: string;
  notes: string;
  file?: File;
}

const DogForm: React.FC<IProps> = (props) => {
  const user: IUser = helperService.getUser();

  const { switchToTab, activeTab } = useStepForm(props.initialTab ?? 0);

  const [isCreatingDog, setIsCreatingDog] = useState<boolean>(false);
  const [isSelectingImage, setIsSelectingImage] = useState<boolean>(false);

  // ── Health Records (vaccine certificates) ─────────────────────────────────
  const [existingAttachments, setExistingAttachments] = useState<IDogAttachment[]>([]);
  const [pendingHealthRecords, setPendingHealthRecords] = useState<IPendingHealthRecord[]>([]);
  const pendingHealthRecordsRef = useRef<IPendingHealthRecord[]>([]);
  const [removingAttachmentId, setRemovingAttachmentId] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState<IDogAttachment | null>(null);
  const [isSavingRecords, setIsSavingRecords] = useState<boolean>(false);

  // Keep ref in sync with state so callbacks always see current value
  useEffect(() => {
    pendingHealthRecordsRef.current = pendingHealthRecords;
  }, [pendingHealthRecords]);

  // Sync existingAttachments from props when the parent provides a fresh dog
  // (e.g. after another component updates the dog and passes new props).
  // We guard against overwriting in-flight removals by checking the ref.
  useEffect(() => {
    if (props.dog?.attachments !== undefined && removingAttachmentId === null) {
      setExistingAttachments(props.dog.attachments);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dog?.attachments]);

  const addPendingRecord = () => {
    if (existingAttachments.length + pendingHealthRecords.length >= 6) return;
    setPendingHealthRecords(prev => [...prev, { type: "", expiry_date: "", notes: "" }]);
  };

  const updatePendingRecord = (index: number, patch: Partial<IPendingHealthRecord>) => {
    setPendingHealthRecords(prev => prev.map((r, i) => i === index ? { ...r, ...patch } : r));
  };

  const removePendingRecord = (index: number) => {
    setPendingHealthRecords(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = async (attachment: IDogAttachment) => {
    if (!props.dog?._id) return;
    setRemovingAttachmentId(attachment.file_url);
    try {
      const updatedDog = await apiService.removeDogAttachment(props.dog._id.toString(), attachment.file_url);
      // Derive the new list from the server's response so we're always in sync with the DB.
      const freshAttachments = (updatedDog?.attachments ?? []) as IDogAttachment[];
      setExistingAttachments(freshAttachments);
      // Notify parent so its dog state stays fresh (prevents stale data on modal reopen).
      if (updatedDog) {
        props.onDogUpdated?.(updatedDog);
      }
    } catch {
      toast.show({ title: "Health Records", content: "Failed to remove certificate. Please try again.", duration: 6000, type: "fail" });
    } finally {
      setRemovingAttachmentId(null);
    }
  };

  const uploadPendingAttachments = async (dogId: string): Promise<IDog | null> => {
    const pending = pendingHealthRecordsRef.current.filter(r => r.file && r.type && r.expiry_date);
    let lastDog: IDog | null = null;
    for (const record of pending) {
      try {
        const result: any = await apiService.uploadFile(record.file!);
        const fileUrl = result?.value ?? result;
        if (fileUrl) {
          lastDog = await apiService.addDogAttachment(dogId, {
            type: record.type as DogVaccineType,
            file_url: fileUrl,
            file_name: record.file!.name,
            file_size: record.file!.size,
            mime_type: record.file!.type || "application/octet-stream",
            expiry_date: record.expiry_date,
            notes: record.notes || undefined,
          });
        }
      } catch {
        toast.show({ title: "Health Records", content: `Failed to upload certificate. You can add it later from your dog's profile.`, duration: 8000, type: "fail" });
      }
    }
    return lastDog;
  };

  const [name, setName] = useState<string>("");
  const [callName, setCallName] = useState<string>("");
  const [prefixTitle, setPrefixTitle] = useState<string>("");
  const [suffixTitle, setSuffixTitle] = useState<string>("");

  const [imgSrc, setImgSrc] = useState<string>();
  const [imgFile, setImgFile] = useState<File>();
  const [imgDimensions, setImgDimensions] = useState<{width: number, height: number}>({width: 0, height: 0});

  const imgRef = useRef<HTMLImageElement>(null);

  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50
  });

  const [userDogs, setUserDogs] = useState<any>([]);

  const [dogDOB, setDogDOB] = useState<Date>();
  const [gender, setGender] = useState<string>(Constants.dog_sex.male);
  const [weight, setWeight] = useState<number>();
  const [heightAtWithers, setHeightAtWithers] = useState<number | null>(null);
  const [heightAtWithersInput, setHeightAtWithersInput] = useState<string>("");
  const [finalResponse, setFinalResponse] = useState<string>("");

  // Owner information state
  const [isAccountOwner, setIsAccountOwner] = useState<boolean>(true);
  const [ownerFirstName, setOwnerFirstName] = useState<string>("");
  const [ownerLastName, setOwnerLastName] = useState<string>("");
  const [ownerLine1, setOwnerLine1] = useState<string>("");
  const [ownerLine2, setOwnerLine2] = useState<string>("");
  const [ownerCity, setOwnerCity] = useState<string>("");
  const [ownerState, setOwnerState] = useState<IOption>({ id: "", value: "" });
  const [ownerCountry, setOwnerCountry] = useState<IOption>({ id: "", value: "" });
  const [ownerPostalCode, setOwnerPostalCode] = useState<string>("");
  const [ownerEmail, setOwnerEmail] = useState<string>("");
  const [ownerPhone, setOwnerPhone] = useState<string>("");
  const [ownerEmergencyContactName, setOwnerEmergencyContactName] = useState<string>("");
  const [ownerEmergencyContactPhone, setOwnerEmergencyContactPhone] = useState<string>("");

  const [breedList, setBreedList] = useState<IBreed[]>([]);
  const [AKCBreedList, setAKCBreedList] = useState<IBreed[]>([]);
  const [UKCBreedList, setUKCBreedList] = useState<IBreed[]>([]);
  const [CKCBreedList, setCKCBreedList] = useState<IBreed[]>([]);
  const [breedListsLoading, setBreedListsLoading] = useState<boolean>(true);

  const registrationNumTypeUKC:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.ukc_registered, value: Constants.registration_number_type.ukc_permanent },
    { id: Constants.registration_number_type.ukc_temporary, value: Constants.registration_number_type.ukc_temporary },
    { id: Constants.registration_number_type.ukc_performance, value: Constants.registration_number_type.ukc_performance_pl }
  ]}, []);

  const registrationNumTypeAKC:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.akc_registered, value: Constants.registration_number_type.akc_registered },
    { id: Constants.registration_number_type.akc_pal, value: Constants.registration_number_type.akc_pal },
    { id: Constants.registration_number_type.akc_canine_partner, value: Constants.registration_number_type.akc_canine_partner },
    { id: Constants.registration_number_type.akc_foreign_reg, value: Constants.registration_number_type.akc_foreign_reg }
  ]}, []);

  const registrationNumTypeCKC:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.ckc_registered, value: "CKC" },
    { id: Constants.registration_number_type.ckc_ws, value: "WS" },
    { id: Constants.registration_number_type.ckc_msc, value: "MSC" },
    { id: Constants.registration_number_type.ckc_ern, value: "ERN" },
    { id: Constants.registration_number_type.ckc_ccn, value: "CCN" },
    { id: Constants.registration_number_type.ckc_pen, value: "PEN" },
    { id: Constants.registration_number_type.ckc_tcn, value: "TCN" },
  ]}, []);

  const registrationNumTypeABI:IOption[] = useMemo(() => { return [
    { id: Constants.sanctioning_club.UKC, value: "ABI" }
  ]}, []);

  const registrationNumTypeISWS:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.isws_registered, value: "ISWS" }
  ]}, []);

  const registrationNumTypePWDCA:IOption[] = useMemo(() => { return [
    { id: "AKC", value: "AKC" },
    { id: "CKC", value: "CKC" }
  ]}, []);

  const registrationNumTypeCPE:IOption[] = useMemo(() => { return [
    { id: "CPE", value: "CPE" }
  ]}, []);

  const registrationNumTypeGCR:IOption[] = useMemo(() => { return [
    { id: "GCR", value: "GCR" }
  ]}, []);

  const registrationNumTypeGENERAL:IOption[] = useMemo(() => { return [
    { id: "General", value: "General" }
  ]}, []);

  const registrationNumTypeC_WAGS:IOption[] = useMemo(() => { return [
    { id: "C-Wags", value: "C-Wags" }
  ]}, []);

  const registrationNumTypeBHA:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.barn_hunt, value: "BH" }
  ]}, []);

  const registrationNumTypeAHBA:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.ahba_registered, value: "AHBA" }
  ]}, []);

  const registrationNumTypeASFA:IOption[] = useMemo(() => { return [
    { id: Constants.registration_number_type.asfa_registered, value: "ASFA" }
  ]}, []);

  // New club registration management state
  const [clubRegistrations, setClubRegistrations] =
    useState<DogClubRegistrationDto[]>([]);

  // Synchronize clubRegistrations state when props.dog changes (after save)
  useEffect(() => {
    if (props.dog?.club_registrations || props.proposedChanges?.club_registrations) {
      const base = props.dog?.club_registrations ?? [];
      const changes = props.proposedChanges?.club_registrations?.map(c => c.new) ?? [];

      const mergedMap = new Map<string, DogClubRegistrationDto>(
        base.map(reg => [reg.sanctioning_club, reg])
      );

      changes.forEach(reg => mergedMap.set(reg.sanctioning_club, reg));

      setClubRegistrations(Array.from(mergedMap.values()));
    }
  }, [props.dog?.club_registrations, props.proposedChanges?.club_registrations]);

  // Registration types for all clubs
  const registrationTypes: Record<string, IOption[]> = useMemo(() => ({
    [Constants.sanctioning_club.UKC]: registrationNumTypeUKC,
    [Constants.sanctioning_club.AKC]: registrationNumTypeAKC,
    [Constants.sanctioning_club.CKC]: registrationNumTypeCKC,
    [Constants.sanctioning_club.ABI]: registrationNumTypeABI,
    [Constants.sanctioning_club.ISWS]: registrationNumTypeISWS,
    [Constants.sanctioning_club.PWDCA]: registrationNumTypePWDCA, // Simple AKC/CKC options
    [Constants.sanctioning_club.CPE]: registrationNumTypeCPE,
    [Constants.sanctioning_club.GCR]: registrationNumTypeGCR,
    [Constants.sanctioning_club.GENERAL]: registrationNumTypeGENERAL,
    [Constants.sanctioning_club.C_WAGS]: registrationNumTypeC_WAGS,
    [Constants.sanctioning_club.BHA]: registrationNumTypeBHA,
    [Constants.sanctioning_club.ASFA]: registrationNumTypeASFA,
    [Constants.sanctioning_club.FH]: [], // 4-H: no registration number needed
    [Constants.sanctioning_club.AHBA]: registrationNumTypeAHBA,
    // NASDA is hidden for now
  }), [registrationNumTypeUKC, registrationNumTypeAKC, registrationNumTypeCKC, registrationNumTypeABI, registrationNumTypeISWS, registrationNumTypePWDCA, registrationNumTypeCPE, registrationNumTypeGCR, registrationNumTypeGENERAL, registrationNumTypeC_WAGS, registrationNumTypeBHA, registrationNumTypeASFA, registrationNumTypeAHBA]);

  // Club management functions
  const toggleClub = (club: string) => {
    // Do not allow to remove existing registrations
    if ((props.dog as any)?.club_registrations?.some((cr: any) => cr.sanctioning_club === club)) {
      return;
    }

    if (clubRegistrations.some((cr) => cr.sanctioning_club === club)) {
      // Remove club
      setClubRegistrations(clubRegistrations.filter(reg => reg.sanctioning_club !== club));
    } else {
      // Add club — pre-populate from existing dog data when editing so only registration
      // number/type needs to be filled in
      setClubRegistrations([...clubRegistrations, {
        sanctioning_club: club,
        registration_type: "",
        registration_number: "",
        call_name: props.dog?.call_name || "",
        prefix_title: props.dog?.prefix_title || "",
        registered_name: props.dog?.registered_name || "",
        suffix_title: props.dog?.suffix_title || "",
        breed: props.dog?.breed || "",
        variety: props.dog?.variety || "",
        variation: ""
      }]);
    }
  };

  const updateClubRegistration = (updatedRegistration: DogClubRegistrationDto) => {
    setClubRegistrations(prevClubs =>
      prevClubs.map(club =>
        club.sanctioning_club === updatedRegistration.sanctioning_club ? updatedRegistration : club
      )
    );
  };

  const removeClubRegistration = (registration: DogClubRegistrationDto) => {
    setClubRegistrations(prevClubs =>
      prevClubs.filter(club => club.sanctioning_club !== registration.sanctioning_club)
    );
  };

  // Get the correct breed list for the sanctioning club
  const getBreedListForClub = (club: string) => {
    switch (club) {
      case Constants.sanctioning_club.AKC:
        return AKCBreedList;
      case Constants.sanctioning_club.UKC:
        return UKCBreedList;
      case Constants.sanctioning_club.CKC:
        return CKCBreedList;
      case Constants.sanctioning_club.GENERAL:
        return UKCBreedList; // Use UKC breed list as default for General
      case Constants.sanctioning_club.C_WAGS:
        return UKCBreedList; // Use UKC breed list for C-Wags
      case Constants.sanctioning_club.BHA:
        return AKCBreedList; // Use AKC breed list for BHA
      case Constants.sanctioning_club.ASFA:
        return AKCBreedList; // Use AKC breed list for ASFA
      case Constants.sanctioning_club.FH:
        return AKCBreedList; // Use AKC breed list for 4-H
      case Constants.sanctioning_club.AHBA:
        return AKCBreedList; // Use AKC breed list for AHBA
      default:
        return breedList; // fallback to general breed list
    }
  };

  const initialOwner: IDogCoownerInfo = {
    coowner_type: "owner",
    coowner_number: "",
    email: "",
    first_name: "",
    last_name: "",
    display_name: "",
    phone: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "",
      postal_code: ""
    }
  };

  const [coOwners, setCoOwners] = useState<IDogCoownerInfo[]>([{ ...initialOwner }]);

  const [countryOfBirth, setCountryOfBirth] = useState<string>("");
  const [breeder, setBreeder] = useState<string>("");
  const [sire, setSire] = useState<string>("");
  const [dam, setDam] = useState<string>("");

  const validateDog = () => {
    let passed:string = "";

    // Owner Information validation (if not using account owner)
    if (!isAccountOwner) {
      if (!ownerFirstName || !ownerLastName) {
        passed = "Please enter the owner's first and last name";
        switchToTab(2); // Owner tab
        return passed;
      }
      if (!ownerLine1 || !ownerCity || !ownerState.id || !ownerCountry.id || !ownerPostalCode) {
        passed = "Please fill in all required owner address fields";
        switchToTab(2); // Owner tab
        return passed;
      }
      if (!ownerEmail) {
        passed = "Please enter the owner's email address";
        switchToTab(2);
        return passed;
      }
      if (!ownerPhone) {
        passed = "Please enter the owner's phone number";
        switchToTab(2);
        return passed;
      }
    }

    // Club Registration validation
    if (clubRegistrations.length === 0) {
      passed = "Please select at least one sanctioning club";
      switchToTab(4); // Club Specific Information tab (now index 4 instead of 3)
      return passed;
    }

    // Validate each club registration
    for (let i = 0; i < clubRegistrations.length; i++) {
      const reg = clubRegistrations[i];

      // C-WAGS specific validation: registration number must be 7 or 8 digits
      if (reg.sanctioning_club === Constants.sanctioning_club.C_WAGS && reg.registration_number) {
        const digitsOnly = reg.registration_number.replace(/\D/g, '');
        if (digitsOnly.length !== 7 && digitsOnly.length !== 8) {
          passed = `C-WAGS registration number must be 7 or 8 digits (currently ${digitsOnly.length})`;
          switchToTab(4);
          return passed;
        }
      }

      if (!reg.breed) {
        passed = `Please select a breed for ${reg.sanctioning_club}`;
        switchToTab(4);
        return passed;
      }

      // For AKC, UKC, etc. - breed must be from the list (no custom/free-typed breeds).
      // CPE, GCR, and General allow custom free-typed breeds saved to the dog profile.
      if (![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.GENERAL, Constants.sanctioning_club.BHA].includes(reg.sanctioning_club)) {
        const breedList = getBreedListForClub(reg.sanctioning_club);
        if (breedList.length > 0) {
          const isValidBreed = breedList.some(b => b.breed.toLowerCase() === reg.breed?.toLowerCase());
          if (!isValidBreed) {
            passed = `Please select a breed from the list for ${reg.sanctioning_club}. Typed breeds are not allowed.`;
            switchToTab(4);
            return passed;
          }
        }
      }

      if (!reg.call_name) {
        passed = `Please enter a call name for ${reg.sanctioning_club}`;
        switchToTab(4);
        return passed;
      }

      // Skip registered name validation for CPE, GCR, General, and 4-H (not required for them)
      if (!reg.registered_name && ![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.GENERAL, Constants.sanctioning_club.FH].includes(reg.sanctioning_club)) {
        passed = `Please enter a registered name for ${reg.sanctioning_club}`;
        switchToTab(4);
        return passed;
      }
    }

    return passed;
  }

  const isEditingDog = () => {
    return props.dog && props.dog._id;
  };

  const getDogId = () => {
    return isEditingDog() ? (props?.dog?._id ?? '') : '';
  };

  const normalizeAkcRegistrationNumber = (registrationNumber?: string): string => {
    if (!registrationNumber) return "";
    return registrationNumber.toUpperCase().replace(/\//g, "");
  };

  const showToast = (title: string, content: string, type: string, errorDetails?: any) => {
    toast.show({
      title: title,
      content: content,
      duration: 10000,
      type: type,
      errorDetails: errorDetails,
    });
  };

  // Explicitly save pending health records for an already-persisted dog.
  // Does NOT close the modal — the user stays on the Health Records tab.
  const handleSaveHealthRecords = async () => {
    const dogId = props.dog?._id?.toString();
    if (!dogId) return;

    const readyToSave = pendingHealthRecordsRef.current.filter(r => r.file && r.type && r.expiry_date);
    if (readyToSave.length === 0) {
      toast.show({ title: "Health Records", content: "Please fill in all fields and choose a file before saving.", duration: 5000, type: "fail" });
      return;
    }

    setIsSavingRecords(true);
    try {
      const updatedDog = await uploadPendingAttachments(dogId);
      if (updatedDog?.attachments) {
        setExistingAttachments(updatedDog.attachments);
      }
      setPendingHealthRecords([]);
      toast.show({ title: "Health Records", content: "Certificate(s) saved successfully.", duration: 4000, type: "success" });
    } finally {
      setIsSavingRecords(false);
    }
  };

  const handleSuccessResponse = async (response: any) => {
    if (response._id) {
      const pending = pendingHealthRecordsRef.current.filter(r => r.file && r.type && r.expiry_date);
      if (pending.length > 0) {
        const updatedDog = await uploadPendingAttachments(response._id.toString());
        // Pass the freshest dog data (with attachments) back to the parent
        props.onFinish(updatedDog ?? response);
      } else {
        props.onFinish(response);
      }
    }
  };

  const editDog = (_dogId: string, payload: DogDto | DogProposedChangesDto) => {
    if (!props.dog) return;
    persistDogUpdate({
      dog: props.dog,
      userId: user._id,
      isAdmin: props.isAdmin,
      eventId: props.eventId,
      proposedChanges: props.proposedChanges,
      payload,
    })
      .then(handleSuccessResponse)
      .catch((e: AxiosError) => {
        showToast("Edit Dog", "Unable to update dog to account", "fail", e);
      })
      .finally(() => {
        setIsCreatingDog(false);
      });
  };

  const createDog = (payload: DogDto) => {
    if (props.onBehalf && props.onBehalfEmail && props.clubId) {
      let _payload:any = {
        ...payload,
        ...{ owner_id: props.onBehalf }
      };

      apiService.createDogByClubManager(props.clubId, _payload).then(handleSuccessResponse).catch((e: AxiosError) => {
        showToast("Add Dog", "Unable to add dog to account", "fail", e);
      }).finally(() => {
        setIsCreatingDog(false);
      });
    } else {
      apiService.createDog(payload).then(handleSuccessResponse).catch((e: AxiosError) => {
        showToast("Add Dog", "Unable to add dog to account", "fail", e);
      }).finally(() => {
        setIsCreatingDog(false);
      });
    }
  };

  const isDuplicateDog = (newDog: DogDto) => {

    let dogs = userDogs.filter((d: IDog) => d._id !== props.dog?._id);

    let hasDuplicateCallName = dogs.filter((d: IDog) => {
      return newDog.call_name && d.call_name === newDog.call_name;
    });

    if (hasDuplicateCallName.length > 0) {
      return true;
    }

    let hasDuplicateRegName = dogs.filter((d: IDog) => {
      return newDog.registered_name && d.registered_name && d.registered_name === newDog.registered_name;
    });

    if (hasDuplicateRegName.length > 0) {
      return true;
    }

    let hasAKCNum = newDog.registration_numbers.filter((n: any) => { return n.name === "AKC" });

    if (hasAKCNum.length > 0) {
      for (let i in dogs) {
        let hasDuplicateAKCNum = dogs[i].registration_numbers.filter((d: any) => {
          return d.name === "AKC" && d.value === hasAKCNum[0].value;
        });

        if (hasDuplicateAKCNum.length > 0) {
          return true;
        }
      }
    }

    let hasUKCNum = newDog.registration_numbers.filter((n: any) => { return n.name === "UKC" });

    if (hasUKCNum.length > 0) {
      for (let i in dogs) {
        let hasDuplicateUKCNum = dogs[i].registration_numbers.filter((d: any) => {
          return d.name === "UKC" && d.value === hasUKCNum[0].value;
        });

        if (hasDuplicateUKCNum.length > 0) {
          return true;
        }
      }
    }

    if (newDog.club_registrations && newDog.club_registrations.length > 0) {
      for (const newReg of newDog.club_registrations) {
        if (!newReg.registration_number) continue;
        const normalizedNewRegistration =
          newReg.sanctioning_club === Constants.sanctioning_club.AKC
            ? normalizeAkcRegistrationNumber(newReg.registration_number)
            : newReg.registration_number;
        for (const existingDog of dogs) {
          const match = existingDog.club_registrations?.find((r: IDogClubRegistration) =>
            r.sanctioning_club === newReg.sanctioning_club &&
            (newReg.sanctioning_club === Constants.sanctioning_club.AKC
              ? normalizeAkcRegistrationNumber(r.registration_number)
              : r.registration_number) === normalizedNewRegistration
          );
          if (match) return true;
        }
      }
    }

    return false;
  }

  const handleDog = () => {
    let _error: string = validateDog();
    const actionTitle = isEditingDog() ? "Edit Dog" : "Add Dog";

    if (_error.length > 0) {
      showToast(actionTitle, _error, "fail");
      return false;
    }

    setIsCreatingDog(true);

    // Get primary registration for backward compatibility
    const primaryRegistration = clubRegistrations.length > 0 ? clubRegistrations[0] : null;

    let payload: any = {
      // NEW: Club-specific registrations
      club_registrations: clubRegistrations.map(reg => ({
        sanctioning_club: reg.sanctioning_club,
        registration_type: reg.registration_type || "",
        registration_number:
          reg.sanctioning_club === Constants.sanctioning_club.AKC
            ? normalizeAkcRegistrationNumber(reg.registration_number || "")
            : (reg.registration_number || ""),
        call_name: reg.call_name || "",
        prefix_title: reg.prefix_title || "",
        registered_name: reg.registered_name || "",
        suffix_title: reg.suffix_title || "",
        breed: reg.breed || "",
        variety: reg.variety || "",
        variation: reg.variation || "",
        needs_measurement: reg.needs_measurement ?? undefined,
        ukc_jump_height_type: reg.sanctioning_club === Constants.sanctioning_club.UKC ? reg.ukc_jump_height_type : undefined,
        akc_obedience_jump_height: reg.sanctioning_club === Constants.sanctioning_club.AKC ? reg.akc_obedience_jump_height : undefined,
        akc_rally_jump_height: reg.sanctioning_club === Constants.sanctioning_club.AKC ? reg.akc_rally_jump_height : undefined,
        akc_agility_jump_height: reg.sanctioning_club === Constants.sanctioning_club.AKC ? reg.akc_agility_jump_height : undefined,
        akc_obedience_preferred_jump_height: reg.sanctioning_club === Constants.sanctioning_club.AKC ? reg.akc_obedience_preferred_jump_height : undefined,
        ckc_obedience_jump_height: reg.sanctioning_club === Constants.sanctioning_club.CKC ? reg.ckc_obedience_jump_height : undefined,
        ckc_rally_jump_height: reg.sanctioning_club === Constants.sanctioning_club.CKC ? reg.ckc_rally_jump_height : undefined,
      })),

      // LEGACY: Keep old fields for backward compatibility (use primary registration)
      breed: primaryRegistration?.breed ?? "",
      variety: primaryRegistration?.variety ?? "",
      registered_name: primaryRegistration?.registered_name || name,
      call_name: primaryRegistration?.call_name || callName,
      prefix_title: primaryRegistration?.prefix_title || prefixTitle,
      suffix_title: primaryRegistration?.suffix_title || suffixTitle,

      // GLOBAL: Information that applies to all sanctioning clubs
      sex: gender,
      dob: dogDOB !== undefined ? new Date(dogDOB.getFullYear(), dogDOB.getMonth(), dogDOB.getDate(), 0, 0, 0) : undefined,
      image_url: props.proposedChanges?.image_url?.new || props.dog?.image_url || "",
      final_response: finalResponse,
      coowners: coOwners.filter((c: IDogCoownerInfo, index: number) => { return c.first_name !== undefined && c.last_name !== undefined && c.first_name !== "" && c.last_name !== ""; }).map((c: IDogCoownerInfo, index: number) => {
        c.display_name = c.first_name +" "+ c.last_name;
        return c;
      }),
      country_of_birth: countryOfBirth,
      breeder: breeder,
      sire: sire,
      dam: dam,
      
      // Owner information - only include if user unchecked "I am the owner"
      owner_info: !isAccountOwner ? {
        first_name: ownerFirstName,
        last_name: ownerLastName,
        address: {
          line1: ownerLine1,
          line2: ownerLine2,
          city: ownerCity,
          state: ownerState.id,
          country: ownerCountry.id,
          postal_code: ownerPostalCode,
        },
        email: ownerEmail,
        phone: ownerPhone,
        emergency_contact_name: ownerEmergencyContactName,
        emergency_contact_phone: ownerEmergencyContactPhone,
      } : undefined,
    };
    
    // Only include registration_numbers if NOT in event registration context
    // (in event context, club_registrations is used instead and registration_numbers may have invalid legacy data)
    // However, backend requires it as an array, so pass empty array if in event context
    if (!props.eventId) {
      payload.registration_numbers = props.dog?.registration_numbers ?? [];
    } else {
      payload.registration_numbers = []; // Required by backend validation but not used in event context
    }

    if (weight && weight > 0) {
      payload.weight = weight;
    }

    if (heightAtWithers && heightAtWithers > 0) {
      payload.height_at_withers = heightAtWithers;
    }

    if (!isEditingDog() && isDuplicateDog(payload)) {
      showToast("Create Dog", "You already have a dog with the same call name, registered name, and/or registration (UKC/AKC) number on this account.", "fail");
      setIsCreatingDog(false);
    } else {
      if (isEditingDog()) {
        // if image is changed, upload it first
        if (imgFile) {
          apiService.uploadFile(imgFile).then((response: any) => {
            if (response.value) {
              payload.image_url = response.value;
            }
            editDog(getDogId(), payload);
          });
        } else {
          editDog(getDogId(), payload);
        }
      } else {
        if (imgFile) {
          apiService.uploadFile(imgFile).then((response: any) => {
            if (response.value) {
              payload.image_url = response.value;
              createDog(payload);
            } else {
              showToast("Image Upload", "There's an issue uploading your dog's profile image, please try again later", "fail");
            }
          });
        } else {
          createDog(payload);
        }
      }
    }
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    let size:number = e.target.files && e.target.files[0]?.size ? e.target.files[0].size : 0;
    let maxSize:number = 1048576 * 8; // 8MB

    if (size > 0) {
      setIsSelectingImage(true);
    }

    if (size > maxSize) {
      toast.show({
        title: "Image Upload",
        content: "Image is too big, please select a smaller image",
        duration: 10000,
        type: "fail"
      });
    } else if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();

      reader.addEventListener('load', () => {
        const imgSrc = reader.result?.toString() || '';
        setImgSrc(imgSrc);

        // Create a temporary image to get dimensions and calculate proper display size
        const tempImg = new Image();
        tempImg.onload = () => {
          const maxDisplayWidth = 400;
          const maxDisplayHeight = 400;

          // Calculate the display dimensions while maintaining aspect ratio
          let displayWidth = tempImg.width;
          let displayHeight = tempImg.height;

          if (displayWidth > maxDisplayWidth) {
            displayHeight = (displayHeight * maxDisplayWidth) / displayWidth;
            displayWidth = maxDisplayWidth;
          }

          if (displayHeight > maxDisplayHeight) {
            displayWidth = (displayWidth * maxDisplayHeight) / displayHeight;
            displayHeight = maxDisplayHeight;
          }

          setImgDimensions({
            width: Math.round(displayWidth),
            height: Math.round(displayHeight)
          });
        };
        tempImg.src = imgSrc;
      });

      reader.readAsDataURL(e.target.files[0]);
    }
  }

  useEffect(() => {
    if (imgRef.current && crop.width > 0 && crop.height > 0) {
      try {
        const canvas = document.createElement("canvas");
        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
        canvas.width = crop.width;
        canvas.height = crop.height;

        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(
            imgRef.current,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
          );

          const base64Image = canvas.toDataURL("image/jpeg", 1);

          // Validate the data URL before processing
          if (base64Image && base64Image.startsWith('data:image/')) {
            const file = dataURLtoFile(base64Image, "image.png");
            setImgFile(file);
          }
        }
      } catch (error) {
        console.error('Error processing crop:', error);
        // Don't break the UI, just log the error
      }
    }
  }, [completedCrop, imgRef, crop]);

  // Hacky fix, imgRef.current doesn't update until setCrop is sent, setCrop isn't set until you move the cropper.
  // Without this, if you upload an image but don't move the cropper, it won't upload the image.
  // IMAGE HACK START
  const pImgSrc = helperService.usePrevious(imgSrc);

  useEffect(() => {
    if ((pImgSrc !== imgSrc) && imgSrc !== undefined) {
      setCrop({x: 0, y: 0, width: 140, height: 140, unit: 'px'});
    }
  }, [imgSrc, pImgSrc, setCompletedCrop]);
  // IMAGE HACK END

  const dataURLtoFile = (dataurl: any, filename: string) => {
    if (!dataurl || typeof dataurl !== 'string') {
      throw new Error('Invalid data URL provided');
    }

    var arr = dataurl.split(',');
    if (arr.length < 2) {
      throw new Error('Invalid data URL format');
    }

    var mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error('Could not extract MIME type from data URL');
    }

    var mime = mimeMatch[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while(n--){
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, {type:mime});
  }

  const getDogs = useCallback(() => {
    apiService.getUserDogs(props.onBehalf || user._id).then((response) => {
      setUserDogs(response);
    });
  }, [props.onBehalf, user._id]);

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    const searchBreeds = () => {
      return new Promise<IBreed[]>((resolve) => {
        apiService.getBreeds().then((response) => {
          let breeds:IBreed[] = [];

          response.forEach((b: IBreed) => {
            breeds.push(b);
          });

          resolve(breeds);
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Dog Breeds",
            content: "Unable to get dog breeds",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });

          resolve([]);
        });
      });
    }

    const searchBreedsBySanctioningClub = (sanctioningClub: string) => {
      return new Promise<IBreed[]>((resolve) => {
        apiService.getBreedsBySanctioningClub(sanctioningClub).then((response) => {
          let breeds:IBreed[] = [];

          response.forEach((b: IBreed) => {
            breeds.push(b);
          });

          resolve(breeds);
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Dog Breeds",
            content: `Unable to get ${sanctioningClub} dog breeds`,
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });

          resolve([]);
        });
      });
    }

    if (!loaded && props.dog) {
      getDogs();

      const hydrateDogFromProps = async() => {
        setBreedListsLoading(true);
        const rawDob = props?.proposedChanges?.dob?.new || props?.dog?.dob;
        const dob = rawDob ? moment(rawDob).toDate() : undefined;

        try {
          let breeds:IBreed[] = await searchBreeds();
          setBreedList(breeds);

          // Load breeds by sanctioning club
          let akcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.AKC);
          setAKCBreedList(akcBreeds);

          let ukcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.UKC);
          setUKCBreedList(ukcBreeds);

          let ckcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.CKC);
          setCKCBreedList(ckcBreeds);
        } finally {
          setBreedListsLoading(false);
        }

        if (props.dog) {
          setCallName(props?.proposedChanges?.call_name?.new || props?.dog?.call_name || "");
          setName(props?.proposedChanges?.registered_name?.new || props?.dog?.registered_name || "");
          setPrefixTitle(props?.proposedChanges?.prefix_title?.new || props?.dog?.prefix_title || "");
          setSuffixTitle(props?.proposedChanges?.suffix_title?.new || props?.dog?.suffix_title || "");
          if (props?.proposedChanges?.sex?.new || props?.dog?.sex) {
            setGender(props?.proposedChanges?.sex?.new || props?.dog?.sex as string);
          }
          setDogDOB(dob);
          setCountryOfBirth(props?.proposedChanges?.country_of_birth?.new || props.dog?.country_of_birth || "");
          setBreeder(props?.proposedChanges?.breeder?.new || props.dog?.breeder || "");
          setSire(props?.proposedChanges?.sire?.new || props.dog?.sire || "");
          setDam(props?.proposedChanges?.dam?.new || props.dog?.dam || "");

          if (props?.dog?.weight || props?.proposedChanges?.weight) {
            setWeight(props?.proposedChanges?.weight?.new || props?.dog?.weight as number);
          }

          if (props?.dog?.height_at_withers || props?.proposedChanges?.height_at_withers) {
            setHeightAtWithers(props?.proposedChanges?.height_at_withers?.new || props?.dog?.height_at_withers as number);
            setHeightAtWithersInput(props?.proposedChanges?.height_at_withers?.new?.toString() || props?.dog?.height_at_withers?.toString() || "");
          }

          if (props?.dog?.final_response || props?.proposedChanges?.final_response) {
            setFinalResponse(props?.proposedChanges?.final_response?.new || props?.dog?.final_response as string);
          }

          if (props?.dog?.coowners || props?.proposedChanges?.coowners) {
            const proposedCoowners = props.proposedChanges?.coowners?.map((c) => c.new) ?? [];

            if (proposedCoowners.length > 0) {
              setCoOwners(proposedCoowners);
            } else {
              setCoOwners(props.dog?.coowners ?? []);
            }
          }

          // Initialize owner information
          if (props.dog?.owner_info) {
            // Dog has explicit owner info - uncheck the box and populate fields
            setIsAccountOwner(false);
            setOwnerFirstName(props.dog.owner_info.first_name || "");
            setOwnerLastName(props.dog.owner_info.last_name || "");
            setOwnerLine1(props.dog.owner_info.address?.line1 || "");
            setOwnerLine2(props.dog.owner_info.address?.line2 || "");
            setOwnerCity(props.dog.owner_info.address?.city || "");
            setOwnerState({
              id: props.dog.owner_info.address?.state || "",
              value: props.dog.owner_info.address?.state || ""
            });
            setOwnerCountry({
              id: props.dog.owner_info.address?.country || "",
              value: props.dog.owner_info.address?.country || ""
            });
            setOwnerPostalCode(props.dog.owner_info.address?.postal_code || "");
            setOwnerEmail(props.dog.owner_info.email || "");
            
            // Ensure phone numbers have country code prefix for proper formatting
            const phone = props.dog.owner_info.phone || "";
            setOwnerPhone(phone && !phone.startsWith("+") ? `+1${phone}` : phone);
            
            setOwnerEmergencyContactName(props.dog.owner_info.emergency_contact_name || "");
            
            const emergencyPhone = props.dog.owner_info.emergency_contact_phone || "";
            setOwnerEmergencyContactPhone(emergencyPhone && !emergencyPhone.startsWith("+") ? `+1${emergencyPhone}` : emergencyPhone);
          } else {
            // No owner info - default to checked (using account owner)
            setIsAccountOwner(true);
          }
        }
      }

      hydrateDogFromProps();
      setExistingAttachments(props.dog?.attachments ?? []);
      setLoaded(true);
    } else {
      const loadBreeds = async() => {
        setBreedListsLoading(true);

        try {
          let breeds:IBreed[] = await searchBreeds();
          setBreedList(breeds);

          // Load breeds by sanctioning club
          let akcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.AKC);
          setAKCBreedList(akcBreeds);

          let ukcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.UKC);
          setUKCBreedList(ukcBreeds);

          let ckcBreeds:IBreed[] = await searchBreedsBySanctioningClub(Constants.sanctioning_club.CKC);
          setCKCBreedList(ckcBreeds);
        } finally {
          setBreedListsLoading(false);
        }
      }

      loadBreeds();
      getDogs();
      setLoaded(true);
    }
  }, [loaded, props.dog, registrationNumTypeUKC, registrationNumTypeAKC, registrationNumTypeABI, registrationNumTypeISWS, registrationNumTypeCPE, registrationNumTypeGCR, getDogs, props.proposedChanges]);

  return(
    <div className="DogForm">
      <div className="title">
        <span>{!!props.dog ? "Edit" : "Add"} a Dog</span>
        {props.onBehalfEmail && <span className="subTitle">On behalf of {props.onBehalfEmail}</span>}
      </div>
      <div className="formWrap">
        <StepForm activeTab={activeTab} next={switchToTab} onSubmit={handleDog} steps={DOG_FORM_STEPS} isLoading={isCreatingDog}>
          <Step>
            <div className="stepTitle">Basic Information</div>
            <p>{user.user_type !== Constants.user_type.club_manager ? `Enter your dog's basic information that applies to all sanctioning clubs.` : `Enter user dog's basic information that applies to this event.`}</p>

            <DatePick label="Date of Birth" maxDate={new Date()} onChange={setDogDOB} value={dogDOB} className="hideDate" />

            <div className="gender">
              <div className="checkboxWrap">
                <label htmlFor="male">
                  <div className="checkmarkWrap">
                    <input type="checkbox" id={"male"} name={Constants.dog_sex.male} checked={gender === Constants.dog_sex.male ?  true : false} onChange={() => { setGender(Constants.dog_sex.male); }} />
                    <span className="checkmark"></span>
                  </div>
                  <span className="name">Male</span>
                </label>
              </div>
              <div className="checkboxWrap">
                <label htmlFor="female">
                  <div className="checkmarkWrap">
                    <input type="checkbox" id={"female"} name={Constants.dog_sex.female} checked={gender === Constants.dog_sex.female ?  true : false} onChange={() => { setGender(Constants.dog_sex.female); }} />
                    <span className="checkmark"></span>
                  </div>
                  <span className="name">Female</span>
                </label>
              </div>
            </div>

            <Input label="Weight (lbs)" icon={scale} id="weight" type="number" step="0.01" min="1" onChange={(e) => { e.stopPropagation(); setWeight(Number(e.target.value)); }} placeholder="What's your dogs weight? (optional)" defaultValue={weight as any} />
            <Input label="Height at Withers (inches, optional)" icon={num} id="heightAtWithers" type="number" step="0.01" min="1" onChange={(e) => {
              e.stopPropagation();
              const value = e.target.value;
              setHeightAtWithersInput(value);
              const numValue = parseFloat(value);
              setHeightAtWithers(isNaN(numValue) ? null : numValue);
            }} placeholder="What's your dogs height at withers? (optional)" value={heightAtWithersInput} />
            <Input label="Final Response (used for nosework/scentwork)" id="finalResponse" type="string" onChange={(e) => { e.stopPropagation(); setFinalResponse(e.target.value); }} placeholder="What's your dogs final response? (optional)" defaultValue={finalResponse} />
            <Input label="Country Of Birth (optional)" type="string" placeholder="What's your dogs country of birth? (optional)" onChange={(e) => { e.stopPropagation(); setCountryOfBirth(e.target.value); }} defaultValue={countryOfBirth} />
          </Step>
          <Step>
            <div className="stepTitle">Breeder Information</div>
            <p>Enter information about your dog's breeding.  <b>This information is optional, but required for Conformation shows and some other event types.</b></p>

            <Input label="Breeder (optional)" type="string" placeholder="Who's your dogs breeder? (optional)" onChange={(e) => { e.stopPropagation(); setBreeder(e.target.value); }} defaultValue={breeder} />
            <Input label="Sire (optional)" type="string" placeholder="Who's your dogs Sire? (optional)" onChange={(e) => { e.stopPropagation(); setSire(e.target.value); }} defaultValue={sire} />
            <Input label="Dam (optional)" type="string" placeholder="Who's your dogs Dam? (optional)" onChange={(e) => { e.stopPropagation(); setDam(e.target.value); }} defaultValue={dam} />
          </Step>
          <Step>
            <React.Fragment>
              <div className="stepTitle">Owner</div>
              <p>Specify the dog's owner information. This will be used on entry forms and registrations.</p>
              
              <Checkbox
                id="isAccountOwner"
                label="I am the owner of this dog"
                value={isAccountOwner}
                onChange={(e) => setIsAccountOwner(e.target.checked)}
                className="ownerCheckbox"
              />

              {!isAccountOwner && (
                <>
                  <Input
                    required
                    label="First Name"
                    onChange={(e) => { e.stopPropagation(); setOwnerFirstName(e.target.value); }}
                    value={ownerFirstName}
                    placeholder="Owner's first name"
                  />
                  <Input
                    required
                    label="Last Name"
                    onChange={(e) => { e.stopPropagation(); setOwnerLastName(e.target.value); }}
                    value={ownerLastName}
                    placeholder="Owner's last name"
                  />
                  <Input
                    required
                    label="Address Line 1"
                    onChange={(e) => { e.stopPropagation(); setOwnerLine1(e.target.value); }}
                    value={ownerLine1}
                    placeholder="Street address"
                  />
                  <Input 
                    label="Address Line 2" 
                    onChange={(e) => { e.stopPropagation(); setOwnerLine2(e.target.value); }} 
                    value={ownerLine2}
                    placeholder="Apt, suite, etc. (optional)"
                  />
                  <Dropdown
                    label="Country"
                    options={[
                      { id: "United States", value: "United States" },
                      { id: "Canada", value: "Canada" }
                    ]}
                    placeholder="Select country"
                    value={ownerCountry}
                    onChange={(e, option) => { 
                      setOwnerCountry(option); 
                      setOwnerState({ id: "", value: "" }); 
                    }}
                  />
                  {ownerCountry.id === "United States" && (
                    <Dropdown
                      label="State"
                      options={States}
                      placeholder="Select state"
                      value={ownerState}
                      onChange={(e, option) => setOwnerState(option)}
                    />
                  )}
                  {ownerCountry.id === "Canada" && (
                    <Dropdown
                      label="Province"
                      options={Provinces}
                      placeholder="Select province"
                      value={ownerState}
                      onChange={(e, option) => setOwnerState(option)}
                    />
                  )}
                  <Input 
                    required 
                    label="City" 
                    onChange={(e) => { e.stopPropagation(); setOwnerCity(e.target.value); }} 
                    value={ownerCity}
                    placeholder="City"
                  />
                  <Input 
                    required 
                    label="Postal / Zip Code" 
                    onChange={(e) => { e.stopPropagation(); setOwnerPostalCode(e.target.value); }} 
                    value={ownerPostalCode}
                    placeholder="Postal / Zip code"
                  />
                  <Input 
                    required 
                    label="Email Address" 
                    type="email"
                    onChange={(e) => { e.stopPropagation(); setOwnerEmail(e.target.value); }} 
                    value={ownerEmail}
                    placeholder="owner@example.com"
                  />
                  <Phone
                    required
                    label="Phone Number"
                    onChange={setOwnerPhone}
                    value={ownerPhone}
                    placeholder="(555) 123-4567"
                  />
                  <Input 
                    label="Emergency Contact Name" 
                    onChange={(e) => { e.stopPropagation(); setOwnerEmergencyContactName(e.target.value); }} 
                    value={ownerEmergencyContactName}
                    placeholder="Emergency contact full name (optional)"
                  />
                  <Phone
                    label="Emergency Contact Phone"
                    onChange={setOwnerEmergencyContactPhone}
                    value={ownerEmergencyContactPhone}
                    placeholder="(555) 123-4567"
                  />
                </>
              )}
            </React.Fragment>
          </Step>
          {<Step>
            <React.Fragment>
              <div className="stepTitle">Co-Owner Information</div>
              <p>Enter each dog co-owners Country, State/Province, and Phone Number. This information is optional, but required for AKC shows.</p>
              {loaded && <OwnerList owners={coOwners} onChange={(newOwners: IDogCoownerInfo[]) => { setCoOwners(newOwners); }} />}
            </React.Fragment>
          </Step>}
          <Step>
            <div className="stepTitle">Club Specific Information</div>
            <p>Select sanctioning clubs and enter registration information for each. <b>You must select at least one sanctioning club.</b></p>

            {/* Club Selection */}
            <div className="clubSelection">
              <div className="clubCheckboxes">
                {Object.keys(registrationTypes).map((club: string) => (
                  <div key={club} className="checkboxWrap">
                    <Checkbox
                      id={`club-${club}`}
                      label={club}
                      value={clubRegistrations.some((cr) => cr.sanctioning_club === club)}
                      disabled={(props.dog as any)?.club_registrations?.some((cr: any) => cr.sanctioning_club === club)}
                      onChange={() => toggleClub(club)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Club Registration Forms */}
            <div className="clubRegistrations">
              {clubRegistrations.map((registration, index) => {
                return (
                  <ClubRegistration
                    key={`${registration.sanctioning_club}-${index}`}
                    clubRegistration={registration}
                    persisted={!!(props.dog as any)?.club_registrations?.some((cr: any) => cr.sanctioning_club === registration.sanctioning_club)}
                    onChange={(updatedRegistration) => updateClubRegistration(updatedRegistration)}
                    onRemove={() => removeClubRegistration(registration)}
                    breeds={getBreedListForClub(registration.sanctioning_club)}
                    registrationTypes={registrationTypes}
                    breedsLoading={breedListsLoading}
                  />
                );
              })}
            </div>

            {/* Help Text */}
            <div className="clubHelp">
              <div className="clubHelpInner">
                <div className="clubHelpContent">
                  <p>Can't find the breed you're looking for? <a href={"mailto:info@eagerdog.com?subject=Missing Dog Breed"}>Contact us</a> and let us know.</p>
                  <p>It is your responsibility to ensure your registration information is correct. Entering an event with wrong registration details can cause your results to not be counted correctly by the sanctioning club.</p>
                </div>
              </div>
            </div>
          </Step>
          <Step>
            <div className="stepTitle">Image</div>
            <p>Upload a profile picture for your dog (optional).</p>

            <div className="dogImage">
              <p>Dog Profile Picture (optional)</p>
              <label className="uploadDog">
                <input type="file" accept="image/*" onChange={onSelectFile} />
                <span>{(imgSrc || getDogId()) ? 'Change Image' : 'Upload Image'}</span>
              </label>
              {props?.dog && (props?.dog?.image_url || props?.proposedChanges?.image_url) && !isSelectingImage && <div className="dog-img-wrap">
                <img src={props?.proposedChanges?.image_url?.new || props?.dog?.image_url as string} alt="Dog Portrait" />
              </div>}
              {!!imgSrc && <div
                className="cropWrap"
                style={{
                  height: imgDimensions.height > 0 ? `${imgDimensions.height}px` : '400px',
                  width: imgDimensions.width > 0 ? `${imgDimensions.width}px` : '400px',
                  padding: '20px'
                }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={c => setCrop(c)}
                  aspect={1}
                  minHeight={50}
                  minWidth={50}
                  circularCrop={true}
                  onComplete={(c) => setCompletedCrop(c)}
                  ruleOfThirds={false}
                >
                  <img
                    ref={imgRef}
                    src={imgSrc}
                    alt="Dog Portait Crop"
                    style={{
                      width: imgDimensions.width > 0 ? `${imgDimensions.width}px` : '400px',
                      height: imgDimensions.height > 0 ? `${imgDimensions.height}px` : '400px',
                      display: 'block'
                    }}
                  />
                </ReactCrop>
              </div>}
            </div>
          </Step>
          <Step>
            <div className="healthRecordsStep">
              <div className="stepTitle">Health Records</div>
              <p>Upload vaccine certificates and health records for your dog (up to 6). These may be required before enrolling in certain courses.</p>

              {/* Existing persisted attachments */}
              {existingAttachments.length > 0 && (
                <div className="healthRecordsList">
                  {existingAttachments.map((att) => {
                    const expired = att.expiry_date && new Date(att.expiry_date) < new Date();
                    const typeLabel = DOG_VACCINE_TYPES.find(t => t.value === att.type)?.label ?? att.type;
                    const isConfirming = confirmingRemoval === att;
                    const isRemoving = removingAttachmentId === att.file_url;
                    return (
                      <div key={att._id ?? att.file_url} className={`healthRecord${expired ? " healthRecordExpired" : ""}`}>
                        <div className="healthRecordInfo">
                          <span className="healthRecordType">{typeLabel}</span>
                          <span className={`healthRecordExpiry${expired ? " expired" : ""}`}>
                            Expires: {new Date(att.expiry_date).toLocaleDateString()}
                            {expired && " — Expired"}
                          </span>
                          {att.notes && <span className="healthRecordNotes">{att.notes}</span>}
                          <a className="healthRecordFile" href={att.file_url} target="_blank" rel="noopener noreferrer">
                            {att.file_name}
                          </a>
                        </div>
                        {isConfirming ? (
                          <div className="healthRecordConfirm">
                            <span className="healthRecordConfirmText">Delete this record?</span>
                            <Button
                              className="healthRecordConfirmYes"
                              disabled={isRemoving}
                              onClick={() => {
                                setConfirmingRemoval(null);
                                handleRemoveExistingAttachment(att);
                              }}
                            >
                              {isRemoving ? "Removing…" : "Yes, delete"}
                            </Button>
                            <Button
                              className="healthRecordConfirmNo"
                              onClick={() => setConfirmingRemoval(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            className="healthRecordRemove"
                            disabled={isRemoving}
                            onClick={() => setConfirmingRemoval(att)}
                          >
                            {isRemoving ? "Removing…" : "Remove"}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending (not yet saved) attachments */}
              {pendingHealthRecords.length > 0 && (
                <div className="healthRecordsPending">
                  {pendingHealthRecords.map((rec, idx) => (
                    <div key={idx} className="healthRecordForm">
                      <div className="healthRecordFormRow">
                        <div className="healthRecordFormField healthRecordTypeField">
                          <Dropdown
                            label="Type *"
                            placeholder="Select type…"
                            options={DOG_VACCINE_TYPES.map(t => ({ id: t.value, value: t.label }))}
                            value={rec.type
                              ? { id: rec.type, value: DOG_VACCINE_TYPES.find(t => t.value === rec.type)?.label || rec.type }
                              : { id: "", value: "" }
                            }
                            onChange={(_e, option: IOption) => updatePendingRecord(idx, { type: option.id as DogVaccineType })}
                          />
                        </div>
                        <div className="healthRecordFormField healthRecordDateField">
                          <DatePick
                            label="Expiry Date *"
                            value={rec.expiry_date ? new Date(rec.expiry_date + "T12:00:00") : null}
                            onChange={(date: Date | null) =>
                              updatePendingRecord(idx, { expiry_date: date ? date.toISOString().substring(0, 10) : "" })
                            }
                          />
                        </div>
                      </div>
                      <div className="healthRecordFormRow healthRecordFormFileRow">
                        <label className="healthRecordFileBtn">
                          <input
                            type="file"
                            accept=".pdf,image/jpeg,image/png"
                            style={{ display: "none" }}
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const maxSize = 8 * 1024 * 1024;
                              if (file.size > maxSize) {
                                toast.show({ title: "Health Records", content: "File must be under 8 MB.", duration: 6000, type: "fail" });
                                return;
                              }
                              updatePendingRecord(idx, { file });
                            }}
                          />
                          {rec.file ? rec.file.name : "Choose File (PDF / JPG / PNG, max 8 MB)"}
                        </label>
                        <Button
                          className="healthRecordRemove"
                          onClick={() => removePendingRecord(idx)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add / limit message */}
              {existingAttachments.length + pendingHealthRecords.length < 6 ? (
                <Button className="healthRecordAddBtn" onClick={addPendingRecord}>
                  + Add Certificate
                </Button>
              ) : (
                <p className="healthRecordMax">Maximum of 6 certificates reached.</p>
              )}

              {/* Save Records button — only available when editing an existing dog */}
              {props.dog?._id && pendingHealthRecords.length > 0 && (
                <Button
                  className="healthRecordSaveBtn"
                  disabled={isSavingRecords}
                  onClick={handleSaveHealthRecords}
                >
                  {isSavingRecords ? "Saving…" : "Save Health Records"}
                </Button>
              )}
              {!props.dog?._id && pendingHealthRecords.length > 0 && (
                <p className="healthRecordSaveHint">Your health records will be saved when you click Finish.</p>
              )}
            </div>
          </Step>
        </StepForm>
      </div>
    </div>
  );
}

export default DogForm;